import { Modal } from '@/components/ui/Modal';
import { Globe, Info, Lock, Server, Shield } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface GDPRSecurityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const GDPRSecurityModal: React.FC<GDPRSecurityModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('gdpr.modal.title')}
      size="lg"
    >
      <div className="space-y-6">
        {/* Introduction */}
        <div className="rounded-xl border border-primary-200 bg-primary-50 p-4">
          <p className="text-sm text-primary-900">
            {t('gdpr.modal.introduction')}
          </p>
        </div>

        {/* Section 1: Chiffrement des données */}
        <div>
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success-100">
              <Lock className="h-5 w-5 text-success-700" />
            </div>
            <h4 className="text-base font-semibold text-neutral-900">
              {t('gdpr.modal.encryption.title')}
            </h4>
          </div>
          <ul className="space-y-2 pl-13">
            <li className="flex items-start gap-2 text-sm text-neutral-700">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
              <span>{t('gdpr.modal.encryption.https')}</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-neutral-700">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
              <span>{t('gdpr.modal.encryption.sensitiveData')}</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-neutral-700">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
              <span>{t('gdpr.modal.encryption.files')}</span>
            </li>
          </ul>
        </div>

        {/* Section 2: Hébergement sécurisé */}
        <div>
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
              <Server className="h-5 w-5 text-blue-700" />
            </div>
            <h4 className="text-base font-semibold text-neutral-900">
              {t('gdpr.modal.hosting.title')}
            </h4>
          </div>
          <ul className="space-y-2 pl-13">
            <li className="flex items-start gap-2 text-sm text-neutral-700">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
              <span>{t('gdpr.modal.hosting.europe')}</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-neutral-700">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
              <span>{t('gdpr.modal.hosting.physicalSecurity')}</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-neutral-700">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
              <span>{t('gdpr.modal.hosting.accessControl')}</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-neutral-700">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
              <span>{t('gdpr.modal.hosting.processes')}</span>
            </li>
          </ul>
          <div className="mt-3 pl-13">
            <a
              href="/legal/privacy"
              className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
            >
              {t('gdpr.modal.hosting.learnMore')} →
            </a>
          </div>
        </div>

        {/* Section 3: Conformité RGPD */}
        <div>
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
              <Globe className="h-5 w-5 text-purple-700" />
            </div>
            <h4 className="text-base font-semibold text-neutral-900">
              {t('gdpr.modal.compliance.title')}
            </h4>
          </div>
          <ul className="space-y-2 pl-13">
            <li className="flex items-start gap-2 text-sm text-neutral-700">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
              <span>{t('gdpr.modal.compliance.gdprCompliant')}</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-neutral-700">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
              <span>{t('gdpr.modal.compliance.dataLocation')}</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-neutral-700">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
              <span>{t('gdpr.modal.compliance.accessControl')}</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-neutral-700">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
              <span>{t('gdpr.modal.compliance.identityValidation')}</span>
            </li>
          </ul>
        </div>

        {/* Section 4: Nos engagements */}
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
          <div className="mb-3 flex items-center gap-3">
            <Shield className="h-5 w-5 text-primary-600" />
            <h4 className="text-base font-semibold text-neutral-900">
              {t('gdpr.modal.commitments.title')}
            </h4>
          </div>
          <p className="text-sm text-neutral-700">
            {t('gdpr.modal.commitments.description')}
          </p>
        </div>

        {/* Footer avec bouton de fermeture */}
        <div className="flex justify-end pt-2">
          <button
            onClick={onClose}
            className="rounded-lg bg-primary-600 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
          >
            {t('common.buttons.understood')}
          </button>
        </div>
      </div>
    </Modal>
  );
};
